import PerformanceMeasureService from '@/services/loadperformancemeasure.service';
import { mapGetters } from 'vuex';
import {
  defaultDateFormat,
  defaultDateTimeFormat,
  defaultPageTitle,
  defaultPriceFormatCurrency,
  defaultUserDateFormat,
  defaultUserNumberFormat,
  defaultUserNumberMaximumFractionDigits, langCodes,
  numberFormats,
} from '@/shared';
import moment from 'moment';

const mixins = {
  data() {
    return {
      faviconPath: './cultbooking.ico',
    };
  },
  computed: {
    ...mapGetters('user', ['settings']),
    ...mapGetters({ appPageTitle: 'pageTitle' }),

    userDateFormat() {
      const { dateFormat } = this.settings;
      return dateFormat || defaultUserDateFormat;
    },
    userNumberFormat() {
      const numberFormat = 'numberFormat' in this.settings
        ? this.settings.numberFormat
        : defaultUserNumberFormat;
      const maximumFractionDigits = 'maximumFractionDigits' in this.settings
        ? this.settings.maximumFractionDigits
        : defaultUserNumberMaximumFractionDigits;
      return {
        numberFormat,
        maximumFractionDigits,
      };
    },
    formattedUserDate() {
      return (date) => (date ? moment(date, defaultDateFormat).format(this.userDateFormat) : '-');
    },
    formattedUserDateTime() {
      return (dateTime, format = defaultDateTimeFormat) => {
        const fdate = this.userDateFormat;
        const separator = ' ';
        const ftime = `${separator}HH:mm:ss [GMT]`;
        return dateTime ? moment.utc(dateTime, format).format(`${fdate}${ftime}`) : '-';
      };
    },
    formattedUserNumber() {
      const userCurrencyCode = this.formattedUserCurrencyFormat?.currency ?? defaultPriceFormatCurrency;
      // eslint-disable-next-line max-len
      return (number = 0, currency = null, maximumFractionDigits = null) => new Intl.NumberFormat(this.userNumberFormat.numberFormat, {
        style: currency ? 'currency' : 'decimal',
        currency: currency ?? userCurrencyCode,
        maximumFractionDigits: maximumFractionDigits ?? this.userNumberFormat.maximumFractionDigits,
        minimumFractionDigits: maximumFractionDigits ?? this.userNumberFormat.maximumFractionDigits,
      }).format(number);
    },
    formattedUserCurrencyFormat() {
      return numberFormats.find((item) => item.code === this.userNumberFormat.numberFormat);
    },
    formattedUnixTime() {
      return (dateTime, format = defaultDateTimeFormat) => (dateTime ? moment.utc(dateTime, format).unix() : '-');
    },
    formattedPercentValue() {
      return (number) => number * 100;
    },
    formattedPercentValueToOne() {
      return (num) => `${this.formattedPercentToRoundAndFixed(num, 1)}%`;
    },
    formattedPercentToRoundAndFixed() {
      return (num, fixed = 0) => {
        const float = parseFloat(num);
        const result = float.toFixed(fixed);
        return result <= float ? result : (result - (0.1 ** fixed)).toFixed(fixed);
      };
    },
    isProductExpired() {
      return (validities) => {
        if (validities.length > 0) {
          const valObj = validities.filter((elem) => {
            if (elem.unlim === true) return elem;
            return valObj;
          });
          if (valObj.length > 0) {
            return true;
          }
          const now = new Date();
          const result = new Date(Math.max.apply(null, validities.map((e) => new Date(e.until))));
          const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const targetDateOnly = new Date(result.getFullYear(), result.getMonth(), result.getDate());
          return targetDateOnly >= nowOnly;
        }
        return true;
      };
    },
    isSingleProductExpired() {
      return (validity, unlimitedText) => {
        if (validity === unlimitedText) {
          return true;
        }
        const targetDate = new Date(validity);
        const now = new Date();
        const targetDateOnly = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
        const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return targetDateOnly >= nowOnly;
      };
    },
    getProductValidity() {
      return (validities, unlimitedText) => {
        if (validities.length > 0) {
          const valObj = validities.filter((elem) => {
            if (elem.unlim === true) return elem;
            return valObj;
          });
          if (valObj.length > 0) {
            return unlimitedText;
          }
          const result = new Date(Math.max.apply(null, validities.map((e) => new Date(e.until))));
          return moment(result).format(this.userDateFormat);
        }
        return '-';
      };
    },
    appTitleLocalized() {
      if (this.appPageTitle == null || !this.appPageTitle) return false;
      if (this.appPageTitle.charAt(0) !== '*') {
        return this.appPageTitle;
      }
      const path = this.appPageTitle.slice(1);
      return this.$te(path) ? this.$t(path).capitalizeAll() : false;
    },
    langCodeForMoment() {
      return (code) => {
        const map = {
          zh: 'zh-cn',
          en: 'en-gb',
        };
        return map[code] || code;
      };
    },
  },
  methods: {
    async pageLoadMeasure(currentRoute, start, startTime, endTime, timeOut = 1500) {
      this.$nextTick(async () => {
        if (currentRoute !== null && currentRoute.meta.loadMeasurement) {
          // Calculate the time of loading the page
          // Subtracting 1500 because setTimeOut value is 1500 in api.service.js file
          const duration = endTime - start - timeOut;
          const pageLoadTime = duration.toFixed(2);

          // Negative page load times are not posted.
          // This sometimes happens during page loading, if the page is reloaded.
          if (+pageLoadTime > 0) {
            const payload = {
              pageLoadStartTime: startTime,
              loadingTime: pageLoadTime,
              pageId: currentRoute.name,
            };
            // Store measurement data into database
            await PerformanceMeasureService.storeData(payload);
          }
        }
      });
    },
    resizeScreen() {
      const width = window.innerWidth;
      return width > 550;
    },
    mediaQueryMatches() {
      const mediaQuery = window.matchMedia('(max-width: 768px)');
      return mediaQuery.matches;
    },
    async openWebsiteLink(website) {
      let value = website;
      const schema = /^https?:\/\//.test(value);
      value = `${schema ? '' : 'http://'}${value}`;
      const url = new URL(value);
      window.open(url.href, '_blank');
    },
    createUniqueId() {
      let date = new Date().getTime();
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
        /[xy]/g,
        (c) => {
          // eslint-disable-next-line no-bitwise
          const r = (date + Math.random() * 16) % 16 | 0;
          date = Math.floor(date / 16);
          // eslint-disable-next-line no-bitwise
          return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        },
      );
    },
    findFirstInvalidLanguageCode(fields) {
      const invalidLangs = Object.entries(fields)
        .filter(([key, value]) => key.startsWith('lang') && value.invalid === true)
        .map(([key, value]) => ({ key, value }));
      return invalidLangs.length > 0 ? invalidLangs[0].key.split('-')[1] : null;
    },
    updateAppFavicon() {
      let link = document.querySelector('link[rel~=\'icon\']');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
      }
      link.href = this.faviconPath;
      document.getElementsByTagName('head')[0].appendChild(link);
    },
    updateAppTitle() {
      const tl = this.appTitleLocalized;
      let title = defaultPageTitle;
      if (tl !== false) {
        title = `${title} - ${tl}`;
      }
      document.querySelector('head title').textContent = title;
    },
    updateMomentMonthNames(momentLangCode) {
      langCodes.forEach((locale) => {
        const langCodeForMoment = this.langCodeForMoment(locale);
        if (momentLangCode === langCodeForMoment) {
          const months = moment.localeData(locale).months();
          const shortMonths = months.map((m) => m.slice(0, 3));
          const shortMonthsTT = this.$te(`calendar.${locale}MonthsShort`) ? JSON.parse(this.$t(`calendar.${locale}MonthsShort`)) : null;
          moment.updateLocale(langCodeForMoment, {
            monthsShort: shortMonthsTT || shortMonths,
          });
        }
      });
    },
  },
};
export default mixins;
